/**
 * Base de ventas: espejo de lo que devuelve `backend/src/modules/base-ventas` (controller.ts, `vistaCarga` de
 * cargas.service.ts y `vistaVersion` de versiones.service.ts). Los tipos de la revisión son los de `types.ts` de allá.
 */

export type Archivo = "ventas" | "clientes";
export const ARCHIVOS: Archivo[] = ["ventas", "clientes"];

export type CampoId =
  | "pedido_id"
  | "pedido_fecha"
  | "pedido_cliente_id"
  | "pedido_monto"
  | "pedido_local"
  | "pedido_canal"
  | "pedido_tipo"
  | "pedido_cupon"
  | "cliente_id"
  | "cliente_nombre"
  | "cliente_email"
  | "cliente_celular"
  | "cliente_optin";

export type Confianza = "alta" | "media" | "revisar";

export type Termino = { signo: 1 | -1; columna: string } | { signo: 1 | -1; constante: number };

/** De dónde sale un campo: una columna (con las de respaldo), una cuenta entre columnas o columnas unidas. */
export type Origen =
  | { tipo: "columna"; columna: string; respaldo?: string[] }
  | { tipo: "cuenta"; terminos: Termino[] }
  | { tipo: "unir"; columnas: string[] };

export type Mapeo = Partial<Record<CampoId, Origen | null>>;

export type FormatoArchivo = "xlsx" | "xls" | "html" | "csv";

/** Un campo en la revisión: la columna, su confianza, tres valores reales y cómo los leemos. */
export interface CampoRevisado {
  campo: CampoId;
  archivo: Archivo;
  etiqueta: string;
  obligatorio: boolean;
  origen: Origen | null;
  /** «Total − Envío», «Nombre + Apellido», «Celular (o Telefono)»; "" sin columna */
  columna: string;
  confianza: Confianza | null;
  /** las columnas del archivo para el desplegable: las candidatas primero */
  opciones: string[];
  /** "" en `leido` = no se pudo leer */
  ejemplos: { crudo: string; leido: string }[];
  conDato: number;
  noLeidos: number;
}

export type MotivoDescarte = "sin_fecha" | "sin_cliente" | "sin_monto" | "monto_cero_o_negativo" | "sin_ficha" | "sin_pedido";

export interface Resumen {
  filasVentas: number;
  filasClientes: number;
  pedidos: number;
  pedidosDescartados: number;
  descartes: Partial<Record<MotivoDescarte, number>>;
  personas: number;
  nuncaCompraron: number;
  compradores: number;
  compradoresSinFicha: number;
  pedidosSinFicha: number;
  pctFacturacionSinFicha: number;
  /** «AAAA-MM-DDTHH:MM», la hora de pared del comercio */
  desde: string | null;
  hasta: string | null;
  facturacion: number;
  conCelular: number;
  conMail: number;
  pctCruce: number;
}

export interface Nota {
  codigo: string;
  titulo?: string;
  texto: string;
}

export interface Revision {
  campos: CampoRevisado[];
  resumen: Resumen;
  frenos: Nota[];
  avisos: Nota[];
  mismoFormato: boolean;
  huella: string;
}

export type EstadoCarga = "subiendo" | "leyendo" | "para_revisar" | "guardando" | "guardada" | "fallida";

export interface ArchivoEnCarga {
  nombre: string;
  bytes: number;
  recibidos: number;
  partes: number;
  completo: boolean;
}

export interface LecturaEnCarga {
  nombre: string;
  formato: FormatoArchivo;
  filaEncabezado: number;
  filas: number;
  columnas: string[];
}

export interface Carga {
  id: string;
  estado: EstadoCarga;
  archivos: Partial<Record<Archivo, ArchivoEnCarga>>;
  lectura: Partial<Record<Archivo, LecturaEnCarga>> | null;
  revision: (Revision & { mapeo: Mapeo; revisadaAt: string }) | null;
  error: { codigo: string; texto: string } | null;
  versionId: string | null;
  autor: string;
  createdAt: string;
  venceAt: string;
}

export type EstadoVersion = "guardando" | "en_uso" | "reemplazada" | "fallida";

export interface Version {
  id: string;
  numero: number;
  estado: EstadoVersion;
  subidaAt: string;
  reemplazadaAt: string | null;
  /** el nombre de quien la subió (o su mail) */
  autor: string;
  archivos: Record<Archivo, { nombre: string; bytes: number; formato: FormatoArchivo }>;
  pedidos: number;
  personas: number;
  compradores: number;
  desde: string | null;
  hasta: string | null;
  descargas: { clientes: boolean; pedidos: boolean };
  /**
   * Lo que el equipo dejó escrito de que el comercio le confirmó que sus clientes aceptaron recibir promociones (fase 4):
   * con eso, al preparar el envío, a quien el archivo no dice si aceptó se lo cuenta como sí. Sólo en la base en uso.
   * Opcional: un backend anterior no lo manda.
   */
  consentimiento?: ConsentimientoDeclarado | null;
}

export interface ConsentimientoDeclarado {
  /** quién y cómo lo confirmó («Cecilia, por WhatsApp, 16/10») */
  como: string;
  /** quién lo cargó en Activa o, si es heredada, quién confirmó que sigue valiendo */
  autor: string;
  at: string;
  /**
   * Desde el 29/10/2026 una declaración nueva lleva su respaldo (la captura o el PDF donde el comercio lo confirmó). En
   * false, una de antes (o heredada de una así): sigue valiendo y se marca «sin respaldo». Opcional: un backend anterior
   * no lo manda.
   */
  conRespaldo?: boolean;
  respaldo?: { nombre: string; tipo: string; bytes: number } | null;
  /** la versión de la que se heredó («¿sigue valiendo?» → «Sí»); null en una nueva */
  heredadaDe?: string | null;
}

/** El archivo que respalda una declaración nueva, como lo manda el navegador (el contenido en base64). */
export interface RespaldoParaSubir {
  nombreArchivo: string;
  contenido: string;
}

/** A cuántos clientes les vuelve «sí» la declaración, con el criterio del envío, y por qué no a los demás. */
export interface AlcanceDeLaDeclaracion {
  clientes: number;
  vuelvenSi: number;
  vuelvenSiConCelular: number;
  yaDecianQueSi: number;
  afuera: { baja: number; dijo_que_no: number; sin_contacto: number };
}

/** Una campaña ya preparada que no toma sola la declaración (el sí se fija al preparar). */
export interface PreparadaConLaBase {
  /** la segmentación */
  id: string;
  nombre: string;
  preparadaAt: string;
  /** se preparó con la versión anterior: para usar la base nueva hay que segmentar de nuevo */
  conLaBaseAnterior: boolean;
}

/** Lo que se mira antes de declarar o de heredar (`GET …/versiones/:id/consentimiento`). */
export interface PanoramaConsentimiento {
  versionId: string;
  numero: number;
  declarada: ConsentimientoDeclarado | null;
  /** la declaración de la versión anterior, si tenía: lo que se pregunta si sigue valiendo */
  anterior: (ConsentimientoDeclarado & { versionId: string; numero: number }) | null;
  alcance: AlcanceDeLaDeclaracion;
  preparadas: PreparadaConLaBase[];
}

/** La base en uso, hasta qué día trae ventas y si está desactualizada (`GET /activa/base-ventas`). */
export interface EstadoBase {
  enUso: (Version & { avisos: Nota[] }) | null;
  /** días entre la última venta y hoy, en la hora del comercio */
  dias: number | null;
  umbralDias: number;
  vieja: boolean;
  /** la carga sin terminar más reciente, para retomarla */
  cargaAbierta: Carga | null;
}
